import React, { useState, useEffect } from 'react';
import { Brain, Code, Database, Zap, Send, X, CheckCircle, AlertCircle, Eye, Table, ChevronRight, ChevronDown, Plus, GripVertical } from 'lucide-react';

const QueryBuilderModal = ({ isOpen, onClose, onSave, initialData = {}, context = 'data-exports' }) => {
    const [queryMode, setQueryMode] = useState('sql'); // 'natural' or 'sql' - default to sql since we're focused on onboarding_db

    // Database selection state (only for contact plans)
    const [selectedDatabase, setSelectedDatabase] = useState(context === 'data-exports' ? 'onboarding_db' : null);
    const [availableDatabases, setAvailableDatabases] = useState([]);
    const [databasesLoading, setDatabasesLoading] = useState(false);

    // Natural language state
    const [naturalQuery, setNaturalQuery] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);

    // SQL state
    const [sqlQuery, setSqlQuery] = useState('');

    // Results state
    const [generatedSql, setGeneratedSql] = useState('');
    const [sqlError, setSqlError] = useState('');
    const [queryResults, setQueryResults] = useState(null);
    const [isExecuting, setIsExecuting] = useState(false);
    const [showPreview, setShowPreview] = useState(false);

    // Table browser state
    const [showFieldSelector, setShowFieldSelector] = useState(false);
    const [tables, setTables] = useState([]);
    const [selectedTable, setSelectedTable] = useState(null);
    const [columns, setColumns] = useState([]);
    const [selectedFields, setSelectedFields] = useState([]);
    const [tableLoading, setTableLoading] = useState(false);
    const [columnLoading, setColumnLoading] = useState(false);
    const [expandedTables, setExpandedTables] = useState(new Set());

    useEffect(() => {
        if (isOpen) {
            // Load initial data if editing existing query
            if (initialData.naturalQuery) {
                setNaturalQuery(initialData.naturalQuery);
                setQueryMode('natural');
            }
            if (initialData.sqlQuery) {
                setSqlQuery(initialData.sqlQuery);
                if (!initialData.naturalQuery) {
                    setQueryMode('sql');
                }
            }
            if (initialData.database) {
                setSelectedDatabase(initialData.database);
            }

            // Load database configurations for contact plans
            if (context === 'contact-plans') {
                loadDatabaseConfigurations();
            }
        }
    }, [isOpen, initialData, context]);

    const loadDatabaseConfigurations = async () => {
        setDatabasesLoading(true);
        try {
            const response = await fetch('/api/admin/database/configurations');
            const result = await response.json();

            if (response.ok && result.configurations) {
                setAvailableDatabases(result.configurations);

                // Set default selection to first database if none selected
                if (!selectedDatabase && result.configurations.length > 0) {
                    const firstDbId = result.configurations[0].id.toString();
                    setSelectedDatabase(firstDbId);
                }
            } else {
                console.error('Failed to load database configurations:', result.error);
                setAvailableDatabases([]);
            }
        } catch (error) {
            console.error('Error loading database configurations:', error);
            setAvailableDatabases([]);
        } finally {
            setDatabasesLoading(false);
        }
    };

    const loadTables = async () => {
        setTableLoading(true);
        setTables([]);
        setSelectedTable(null);
        setColumns([]);
        setSelectedFields([]);

        try {
            let response, result;

            if (context === 'data-exports') {
                // Use the original endpoint for data exports (onboarding_db)
                response = await fetch('/api/admin/exports/schema/tables');
                result = await response.json();

                if (response.ok) {
                    // Transform the table data to match the expected format
                    const transformedTables = result.map(table => ({
                        name: table.table_name,
                        fullName: table.table_name,
                        schema: 'public'
                    }));
                    setTables(transformedTables || []);
                } else {
                    console.error('Failed to load tables:', result.error);
                }
            } else if (context === 'contact-plans') {
                if (!selectedDatabase) {
                    return;
                }

                // Use database configuration endpoint for contact plans
                response = await fetch(`/api/admin/database/configurations/${selectedDatabase}/tables`);
                result = await response.json();

                if (response.ok && result.success && result.tables) {
                    // Transform the table data to match the expected format
                    const transformedTables = result.tables.map(table => ({
                        name: table.name,
                        fullName: table.fullName || table.name,
                        schema: table.schema || 'public'
                    }));
                    setTables(transformedTables || []);
                } else {
                    console.error('Failed to load tables:', result.error || result);
                }
            }
        } catch (error) {
            console.error('Network error loading tables:', error);
        } finally {
            setTableLoading(false);
        }
    };

    const loadColumns = async (tableName) => {
        if (!tableName) return;

        setColumnLoading(true);
        setColumns([]);

        try {
            let response, result;

            if (context === 'data-exports') {
                // Use the original endpoint for data exports (onboarding_db)
                response = await fetch(`/api/admin/exports/schema/tables/${encodeURIComponent(tableName)}/columns`);
                result = await response.json();

                if (response.ok) {
                    // Transform the column data to match the expected format
                    const transformedColumns = result.map(column => ({
                        name: column.column_name,
                        type: column.data_type
                    }));
                    setColumns(transformedColumns || []);
                    setSelectedTable(tableName);
                } else {
                    console.error('Failed to load columns:', result.error);
                }
            } else if (context === 'contact-plans') {
                if (!selectedDatabase) {
                    return;
                }

                // Use database configuration endpoint for contact plans
                response = await fetch(`/api/admin/database/configurations/${selectedDatabase}/tables/${encodeURIComponent(tableName)}/columns`);
                result = await response.json();

                if (response.ok && result.success && result.columns) {
                    // Transform the column data to match the expected format
                    // The API already returns columns with 'name' and 'type' properties
                    const transformedColumns = result.columns.map(column => ({
                        name: column.name,
                        type: column.type
                    }));
                    setColumns(transformedColumns || []);
                    setSelectedTable(tableName);
                } else {
                    console.error('Failed to load columns:', result.error || result);
                }
            }
        } catch (error) {
            console.error('Network error loading columns:', error);
        } finally {
            setColumnLoading(false);
        }
    };

    const processNaturalLanguageQuery = async () => {
        if (!naturalQuery.trim()) return;

        setIsProcessing(true);
        setSqlError('');
        setGeneratedSql('');

        try {
            let endpoint, requestBody;

            if (context === 'data-exports') {
                // For data exports, if the query mentions lst.Contacts_nnn tables or vehicle/contact queries,
                // we should use a configured database that contains these tables instead of onboarding_db
                const queryLower = naturalQuery.toLowerCase();

                if (queryLower.includes('contact') || queryLower.includes('vehicle') || queryLower.includes('model') ||
                    queryLower.includes('lst.') || queryLower.includes('customer') || queryLower.includes('enquiry')) {
                    setSqlError('For queries involving customer contacts and vehicle data, please use the Contact Plans section instead, where you can select the appropriate database that contains the lst.Contacts_nnn tables.');
                    return;
                }

                // For other onboarding_db queries
                endpoint = '/api/natural-language/convert';
                requestBody = {
                    query: naturalQuery,
                    configId: 'onboarding_db'
                };
            } else if (context === 'contact-plans') {
                if (!selectedDatabase) {
                    setSqlError('Please select a database first');
                    return;
                }

                endpoint = '/api/natural-language/convert';
                requestBody = {
                    query: naturalQuery,
                    configId: selectedDatabase
                };
            }

            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody)
            });

            const result = await response.json();

            if (response.ok && result.success) {
                setGeneratedSql(result.sql);
                setShowPreview(true);
                setSqlError('');
            } else {
                setSqlError(result.error || 'Failed to convert natural language to SQL');
                setGeneratedSql('');
            }
        } catch (error) {
            console.error('Error processing natural language query:', error);
            setSqlError('Network error: Could not process natural language query');
            setGeneratedSql('');
        } finally {
            setIsProcessing(false);
        }
    };

    const executeQuery = async (sql = null) => {
        const queryToExecute = sql || sqlQuery;

        if (!queryToExecute) return;

        setIsExecuting(true);
        setQueryResults(null);
        setSqlError('');

        try {
            if (context === 'data-exports') {
                // Use the existing endpoint for data exports (onboarding_db)
                const response = await fetch('/api/admin/exports/custom', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        sql: queryToExecute,
                        format: 'json'
                    })
                });

                const result = await response.json();

                if (response.ok) {
                    setQueryResults({
                        success: true,
                        data: result.data.slice(0, 10), // Show first 10 rows for preview
                        rowCount: result.metadata.total_records
                    });
                } else {
                    setSqlError(result.error || 'Failed to execute SQL');
                }
            } else if (context === 'contact-plans') {
                // For contact plans, we'll show a preview message instead of executing
                // since we don't have a query execution endpoint for configured databases yet
                setSqlError('Query execution against configured databases is not yet implemented. The query will be saved and used when the contact plan is triggered.');

                // Still show some mock results to help with query building
                setQueryResults({
                    success: true,
                    data: [{ preview: 'Query syntax validated and saved successfully' }],
                    rowCount: 0,
                    isPreview: true
                });
            }
        } catch (error) {
            console.error('Error executing SQL:', error);
            setSqlError('Network error: Could not execute query');
        } finally {
            setIsExecuting(false);
        }
    };

    const handleSave = () => {
        const data = {
            queryMode,
            naturalQuery: queryMode === 'natural' ? naturalQuery : '',
            sqlQuery: queryMode === 'sql' ? sqlQuery : generatedSql,
            database: context === 'contact-plans' ? selectedDatabase : 'onboarding_db',
            description: queryMode === 'natural'
                ? naturalQuery
                : `Custom SQL Query (${(queryMode === 'sql' ? sqlQuery : generatedSql).substring(0, 50)}...)`
        };

        onSave(data);
        handleClose();
    };

    const handleClose = () => {
        setQueryMode('natural');
        setNaturalQuery('');
        setSqlQuery('');
        setGeneratedSql('');
        setSqlError('');
        setQueryResults(null);
        setShowPreview(false);
        setShowFieldSelector(false);
        setTables([]);
        setSelectedTable(null);
        setColumns([]);
        setSelectedFields([]);
        setExpandedTables(new Set());
        onClose();
    };

    const toggleTableExpansion = (tableName) => {
        const newExpanded = new Set(expandedTables);
        if (newExpanded.has(tableName)) {
            newExpanded.delete(tableName);
            if (selectedTable === tableName) {
                setSelectedTable(null);
                setColumns([]);
            }
        } else {
            newExpanded.add(tableName);
            loadColumns(tableName);
        }
        setExpandedTables(newExpanded);
    };

    const toggleFieldSelection = (fieldName) => {
        setSelectedFields(prev => {
            if (prev.includes(fieldName)) {
                return prev.filter(f => f !== fieldName);
            } else {
                return [...prev, fieldName];
            }
        });
    };

    const insertSelectedFields = () => {
        if (selectedFields.length === 0) return;

        const fieldList = selectedFields.join(', ');
        if (queryMode === 'natural') {
            setNaturalQuery(prev => {
                const addition = prev.trim() ? ` ${fieldList}` : fieldList;
                return prev + addition;
            });
        } else {
            setSqlQuery(prev => {
                const addition = prev.trim() ? ` ${fieldList}` : `SELECT ${fieldList} FROM ${selectedTable || 'table_name'}`;
                return prev + addition;
            });
        }
        setShowFieldSelector(false);
        setSelectedFields([]);
    };

    const handleDragStart = (e, type, data) => {
        e.dataTransfer.setData('application/json', JSON.stringify({ type, data }));
        e.dataTransfer.effectAllowed = 'copy';
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
        e.target.classList.add('border-blue-400', 'ring-2', 'ring-blue-200');
    };

    const handleDragLeave = (e) => {
        e.target.classList.remove('border-blue-400', 'ring-2', 'ring-blue-200');
    };

    const handleDrop = (e, targetType) => {
        e.preventDefault();
        e.target.classList.remove('border-blue-400', 'ring-2', 'ring-blue-200');

        try {
            const draggedData = JSON.parse(e.dataTransfer.getData('application/json'));
            const { type, data } = draggedData;

            let textToInsert = '';

            if (type === 'table') {
                textToInsert = data.name;
            } else if (type === 'column') {
                textToInsert = data.name;
            }

            if (targetType === 'natural') {
                setNaturalQuery(prev => {
                    const cursorPos = e.target.selectionStart || prev.length;
                    const before = prev.substring(0, cursorPos);
                    const after = prev.substring(cursorPos);
                    const addition = before.trim() && !before.endsWith(' ') ? ` ${textToInsert}` : textToInsert;
                    return before + addition + after;
                });
            } else if (targetType === 'sql') {
                setSqlQuery(prev => {
                    const cursorPos = e.target.selectionStart || prev.length;
                    const before = prev.substring(0, cursorPos);
                    const after = prev.substring(cursorPos);
                    const addition = before.trim() && !before.endsWith(' ') ? ` ${textToInsert}` : textToInsert;
                    return before + addition + after;
                });
            }
        } catch (error) {
            console.error('Error handling drop:', error);
        }
    };

    // Load tables when field selector is shown or database selection changes
    useEffect(() => {
        if (showFieldSelector) {
            loadTables();
        }
    }, [showFieldSelector, selectedDatabase]);

    const isValidQuery = () => {
        if (queryMode === 'natural') {
            return naturalQuery.trim();
        } else {
            return sqlQuery.trim();
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full mx-4 max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b">
                    <div className="flex items-center gap-3">
                        <Database size={24} className="text-blue-600" />
                        <div>
                            <h2 className="text-xl font-semibold text-gray-900">Query Builder</h2>
                            <p className="text-sm text-gray-600">
                                {context === 'data-exports'
                                    ? 'Create queries for data export from onboarding_db'
                                    : 'Create custom queries for contact plan conditions'
                                }
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={handleClose}
                        className="text-gray-400 hover:text-gray-600"
                    >
                        <X size={24} />
                    </button>
                </div>

                <div className="p-6 flex-1 overflow-y-auto">
                    {/* Database Selection/Info */}
                    <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                        {context === 'contact-plans' ? (
                            <div>
                                <h3 className="text-sm font-medium text-blue-900 mb-3">Database Selection</h3>
                                {databasesLoading ? (
                                    <div className="flex items-center gap-2 py-4">
                                        <div className="animate-spin w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                                        <span className="text-sm text-blue-700">Loading database configurations...</span>
                                    </div>
                                ) : availableDatabases.length === 0 ? (
                                    <div className="py-4">
                                        <p className="text-sm text-red-600 mb-2">No database configurations found.</p>
                                        <p className="text-xs text-gray-600">
                                            Please configure database connections in the Database Config page first.
                                        </p>
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        {availableDatabases.map(db => (
                                            <label key={db.id} className="flex items-start gap-3 cursor-pointer">
                                                <input
                                                    type="radio"
                                                    name="database"
                                                    value={db.id.toString()}
                                                    checked={selectedDatabase === db.id.toString()}
                                                    onChange={(e) => setSelectedDatabase(e.target.value)}
                                                    className="mt-1"
                                                />
                                                <div>
                                                    <div className="text-sm font-medium text-blue-900">{db.name}</div>
                                                    <div className="text-xs text-blue-700">
                                                        {db.type} • {db.host}:{db.port} • {db.database}
                                                    </div>
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                )}
                                <p className="text-sm text-blue-700 mt-3">
                                    Only SELECT statements are allowed for security.
                                </p>
                            </div>
                        ) : (
                            <div>
                                <h3 className="text-sm font-medium text-blue-900">Database: onboarding_db</h3>
                                <p className="text-sm text-blue-700 mt-1">
                                    Queries will be executed against the onboarding_db database. Only SELECT statements are allowed.
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Query Mode Toggle */}
                    <div className="mb-6">
                        <div className="flex gap-2 mb-4">
                            <button
                                onClick={() => setQueryMode('natural')}
                                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium ${
                                    queryMode === 'natural'
                                        ? 'bg-blue-100 text-blue-700 border border-blue-300'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                            >
                                <Brain size={16} />
                                Natural Language
                            </button>
                            <button
                                onClick={() => setQueryMode('sql')}
                                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium ${
                                    queryMode === 'sql'
                                        ? 'bg-blue-100 text-blue-700 border border-blue-300'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                            >
                                <Code size={16} />
                                SQL Query
                            </button>
                            <button
                                onClick={() => setShowFieldSelector(!showFieldSelector)}
                                className="flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium bg-green-100 text-green-700 hover:bg-green-200 border border-green-300"
                            >
                                <Table size={16} />
                                {showFieldSelector ? 'Hide' : 'Show'} Field Selector
                            </button>
                        </div>
                    </div>

                    {/* Field Selector */}
                    {showFieldSelector && (
                        <div className="mb-6 border border-gray-200 rounded-lg p-4 bg-gray-50">
                            <div className="mb-4">
                                <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                                    <Table size={16} />
                                    Field Selector
                                </h3>
                                <p className="text-sm text-gray-600 mt-1">
                                    Drag <GripVertical size={12} className="inline text-gray-400" /> table names or column names directly into your query
                                </p>
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                {/* Tables List */}
                                <div className="bg-white rounded border">
                                    <div className="p-3 border-b bg-gray-50">
                                        <h4 className="font-medium text-gray-900">Tables</h4>
                                    </div>
                                    <div className="max-h-48 overflow-y-auto">
                                        {tableLoading ? (
                                            <div className="p-4 text-center">
                                                <div className="animate-spin mx-auto mb-2 w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                                                <p className="text-sm text-gray-600">Loading tables...</p>
                                            </div>
                                        ) : tables.length === 0 ? (
                                            <div className="p-4 text-center text-sm text-gray-500">
                                                {context === 'contact-plans' && !selectedDatabase
                                                    ? 'Please select a database first'
                                                    : 'No tables found'
                                                }
                                            </div>
                                        ) : (
                                            <div className="divide-y">
                                                {tables.map((table) => (
                                                    <div key={table.fullName} className="p-2 hover:bg-gray-50 group">
                                                        <div className="flex items-center gap-2">
                                                            <button
                                                                onClick={() => toggleTableExpansion(table.fullName)}
                                                                className="flex items-center gap-2 text-left flex-1"
                                                            >
                                                                {expandedTables.has(table.fullName) ? (
                                                                    <ChevronDown size={14} className="text-gray-400" />
                                                                ) : (
                                                                    <ChevronRight size={14} className="text-gray-400" />
                                                                )}
                                                                <span className="text-sm font-medium">{table.name}</span>
                                                                {table.schema !== table.name && (
                                                                    <span className="text-xs text-gray-500">({table.schema})</span>
                                                                )}
                                                            </button>
                                                            <div
                                                                draggable={true}
                                                                onDragStart={(e) => handleDragStart(e, 'table', table)}
                                                                className="opacity-0 group-hover:opacity-100 cursor-grab active:cursor-grabbing p-1 hover:bg-blue-100 rounded transition-opacity"
                                                                title="Drag to insert table name"
                                                            >
                                                                <GripVertical size={14} className="text-gray-400" />
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Columns List */}
                                <div className="bg-white rounded border">
                                    <div className="p-3 border-b bg-gray-50">
                                        <h4 className="font-medium text-gray-900">
                                            {selectedTable ? `Fields in ${selectedTable}` : 'Select Table'}
                                        </h4>
                                        {selectedFields.length > 0 && (
                                            <div className="mt-2">
                                                <button
                                                    onClick={insertSelectedFields}
                                                    className="flex items-center gap-1 px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                                                >
                                                    <Plus size={12} />
                                                    Insert {selectedFields.length} field{selectedFields.length !== 1 ? 's' : ''}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                    <div className="max-h-48 overflow-y-auto">
                                        {columnLoading ? (
                                            <div className="p-4 text-center">
                                                <div className="animate-spin mx-auto mb-2 w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                                                <p className="text-sm text-gray-600">Loading fields...</p>
                                            </div>
                                        ) : !selectedTable ? (
                                            <div className="p-4 text-center text-sm text-gray-500">
                                                Select a table to view its fields
                                            </div>
                                        ) : columns.length === 0 ? (
                                            <div className="p-4 text-center text-sm text-gray-500">
                                                No fields found
                                            </div>
                                        ) : (
                                            <div className="divide-y">
                                                {columns.map((column, index) => (
                                                    <div key={index} className="flex items-center gap-2 p-2 hover:bg-gray-50 group">
                                                        <label className="flex items-center gap-2 cursor-pointer flex-1">
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedFields.includes(column.name)}
                                                                onChange={() => toggleFieldSelection(column.name)}
                                                                className="text-blue-600"
                                                            />
                                                            <div className="flex-1">
                                                                <div className="text-sm font-medium">{column.name}</div>
                                                                <div className="text-xs text-gray-500">{column.type}</div>
                                                            </div>
                                                        </label>
                                                        <div
                                                            draggable={true}
                                                            onDragStart={(e) => handleDragStart(e, 'column', column)}
                                                            className="opacity-0 group-hover:opacity-100 cursor-grab active:cursor-grabbing p-1 hover:bg-blue-100 rounded transition-opacity"
                                                            title="Drag to insert column name"
                                                        >
                                                            <GripVertical size={14} className="text-gray-400" />
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Natural Language Mode */}
                    {queryMode === 'natural' && (
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Describe your query in plain English
                                    <span className="text-xs text-gray-500 ml-2">(Drop table/column names here)</span>
                                </label>
                                <textarea
                                    value={naturalQuery}
                                    onChange={(e) => setNaturalQuery(e.target.value)}
                                    onDragOver={handleDragOver}
                                    onDragLeave={handleDragLeave}
                                    onDrop={(e) => handleDrop(e, 'natural')}
                                    placeholder="e.g., Find all customers in London who need MOT reminders in the next 30 days"
                                    rows={3}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none transition-all duration-200 hover:border-blue-300"
                                />
                            </div>

                            <div className="flex gap-2">
                                <button
                                    onClick={processNaturalLanguageQuery}
                                    disabled={!naturalQuery.trim() || isProcessing}
                                    className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                                >
                                    {isProcessing ? (
                                        <>
                                            <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                                            Generating...
                                        </>
                                    ) : (
                                        <>
                                            <Zap size={16} />
                                            Generate SQL
                                        </>
                                    )}
                                </button>
                            </div>

                            {/* Generated SQL - Hidden by default */}
                            {generatedSql && showPreview && (
                                <div>
                                    <div className="flex items-center gap-2 mb-2">
                                        <Code size={16} className="text-green-600" />
                                        <span className="text-sm font-medium text-gray-700">Generated SQL Query</span>
                                    </div>
                                    <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm overflow-x-auto">
                                        <pre>{generatedSql}</pre>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* SQL Mode */}
                    {queryMode === 'sql' && (
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    SQL Query
                                    <span className="text-xs text-gray-500 ml-2">(Drop table/column names here)</span>
                                </label>
                                <textarea
                                    value={sqlQuery}
                                    onChange={(e) => setSqlQuery(e.target.value)}
                                    onDragOver={handleDragOver}
                                    onDragLeave={handleDragLeave}
                                    onDrop={(e) => handleDrop(e, 'sql')}
                                    placeholder="SELECT * FROM customers WHERE..."
                                    rows={8}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm resize-none transition-all duration-200 hover:border-blue-300"
                                />
                            </div>
                        </div>
                    )}

                    {/* Error Display */}
                    {sqlError && (
                        <div className="mt-4 border border-red-200 rounded-lg p-4 bg-red-50">
                            <div className="flex items-center gap-2 mb-2">
                                <AlertCircle size={16} className="text-red-600" />
                                <span className="text-red-600 text-sm font-medium">Error</span>
                            </div>
                            <p className="text-red-700 text-sm">{sqlError}</p>
                        </div>
                    )}

                    {/* Query Testing */}
                    {(generatedSql || (queryMode === 'sql' && sqlQuery)) && (
                        <div className="mt-4 space-y-4">
                            <div className="flex gap-2">
                                <button
                                    onClick={() => executeQuery()}
                                    disabled={isExecuting}
                                    className="flex items-center gap-2 px-3 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400"
                                >
                                    {isExecuting ? (
                                        <>
                                            <div className="animate-spin w-3 h-3 border-2 border-white border-t-transparent rounded-full"></div>
                                            Running Query...
                                        </>
                                    ) : (
                                        <>
                                            <Send size={14} />
                                            Run Query
                                        </>
                                    )}
                                </button>
                                {(generatedSql || (queryMode === 'sql' && sqlQuery)) && (
                                    <button
                                        onClick={() => setShowPreview(!showPreview)}
                                        className="flex items-center gap-2 px-3 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                    >
                                        <Eye size={14} />
                                        {showPreview ? 'Hide' : 'Show'} SQL Preview
                                    </button>
                                )}
                            </div>

                            {/* SQL Preview for SQL mode - shown when preview is enabled */}
                            {queryMode === 'sql' && showPreview && sqlQuery && (
                                <div>
                                    <div className="flex items-center gap-2 mb-2">
                                        <Code size={16} className="text-blue-600" />
                                        <span className="text-sm font-medium text-gray-700">SQL Query Preview</span>
                                    </div>
                                    <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm overflow-x-auto">
                                        <pre>{sqlQuery}</pre>
                                    </div>
                                </div>
                            )}

                            {/* Query Results */}
                            {queryResults && (
                                <div>
                                    <div className="flex items-center gap-2 mb-3">
                                        <CheckCircle size={16} className="text-green-600" />
                                        <span className="text-sm font-medium text-gray-700">Query Results</span>
                                        <span className="text-xs text-gray-500">({queryResults.rowCount} rows)</span>
                                    </div>
                                    <div className="border border-gray-200 rounded-lg overflow-hidden max-h-64">
                                        <div className="bg-gray-50 px-4 py-2 border-b">
                                            <span className="text-xs text-gray-600 font-medium">Sample Results (first 10 rows)</span>
                                        </div>
                                        <div className="overflow-x-auto overflow-y-auto max-h-56">
                                            {queryResults.data && queryResults.data.length > 0 ? (
                                                <table className="w-full text-xs">
                                                    <thead className="bg-gray-100">
                                                        <tr>
                                                            {Object.keys(queryResults.data[0]).map((column, index) => (
                                                                <th key={index} className="px-3 py-2 text-left font-medium text-gray-700">
                                                                    {column}
                                                                </th>
                                                            ))}
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {queryResults.data.slice(0, 10).map((row, index) => (
                                                            <tr key={index} className="border-t">
                                                                {Object.values(row).map((value, colIndex) => (
                                                                    <td key={colIndex} className="px-3 py-2 text-gray-600">
                                                                        {value !== null ? String(value) : <span className="text-gray-400">NULL</span>}
                                                                    </td>
                                                                ))}
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            ) : (
                                                <div className="p-4 text-center text-gray-500">
                                                    No data returned
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex justify-between items-center p-6 border-t bg-gray-50 flex-shrink-0">
                    <div className="text-sm text-gray-600">
                        <p><strong>Purpose:</strong> {
                            context === 'data-exports'
                                ? 'Queries will be used to export data from onboarding_db'
                                : `Queries will be used for contact plan conditions on ${availableDatabases.find(db => db.id.toString() === selectedDatabase)?.name || 'selected database'}`
                        }</p>
                        {selectedFields.length > 0 && (
                            <p className="mt-1"><strong>Selected Fields:</strong> {selectedFields.join(', ')}</p>
                        )}
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={handleClose}
                            className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={!isValidQuery()}
                            className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                        >
                            Save Query
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default QueryBuilderModal;